import { useState, useEffect } from 'react';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'usePerformancePhase' });

export type PerformancePhase = 'planning' | 'foundation' | 'execution' | 'impact' | 'review_prep';

interface PerformancePhaseResult {
  currentPhase: PerformancePhase;
  monthsElapsed: number;
  totalMonths: number;
  daysIntoPhase: number;
  daysRemainingInPhase: number;
  phaseProgress: number;
  nextPhase: PerformancePhase | null;
  isTransitioning: boolean;
}

// Phase month ranges (1-indexed)
const PHASE_RANGES: Record<PerformancePhase, [number, number]> = {
  planning: [1, 2],
  foundation: [3, 4],
  execution: [5, 8],
  impact: [9, 10],
  review_prep: [11, 12]
};

const PHASE_ORDER: PerformancePhase[] = ['planning', 'foundation', 'execution', 'impact', 'review_prep'];

export const usePerformancePhase = (focus: any): PerformancePhaseResult => {
  const [result, setResult] = useState<PerformancePhaseResult>({
    currentPhase: 'planning',
    monthsElapsed: 0,
    totalMonths: 12,
    daysIntoPhase: 0,
    daysRemainingInPhase: 60,
    phaseProgress: 0,
    nextPhase: 'foundation',
    isTransitioning: false
  });

  useEffect(() => {
    if (!focus?.start_date || !focus?.end_date) {
      logger.debug('Missing start or end date for performance review focus');
      return;
    }

    try {
      const startDate = new Date(focus.start_date);
      const endDate = new Date(focus.end_date);
      const today = new Date();

      // Ensure we're working with date-only (no time component)
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Calculate total duration
      const totalMs = endDate.getTime() - startDate.getTime();
      const totalDays = Math.round(totalMs / (1000 * 60 * 60 * 24));
      const calculatedTotalMonths = Math.round(totalDays / 30.44); // Average days per month

      // Calculate elapsed time
      const elapsedMs = today.getTime() - startDate.getTime();
      const elapsedDays = Math.max(0, Math.floor(elapsedMs / (1000 * 60 * 60 * 24)));
      const calculatedMonthsElapsed = Math.floor(elapsedDays / 30.44);

      // Determine current phase based on months elapsed (1-indexed)
      const currentMonthNumber = calculatedMonthsElapsed + 1;
      let currentPhase: PerformancePhase = 'planning';
      
      for (const [phase, [start, end]] of Object.entries(PHASE_RANGES)) {
        if (currentMonthNumber >= start && currentMonthNumber <= end) {
          currentPhase = phase as PerformancePhase;
          break;
        }
      }

      // Handle cases where we're past the standard 12 months
      if (currentMonthNumber > 12) {
        currentPhase = 'review_prep';
      }

      // Calculate phase-specific metrics
      const phaseRange = PHASE_RANGES[currentPhase];
      const phaseStartMonth = phaseRange[0];
      const phaseEndMonth = phaseRange[1];
      const phaseMonths = phaseEndMonth - phaseStartMonth + 1;
      
      // Days since phase started
      const phaseStartDays = (phaseStartMonth - 1) * 30.44;
      const daysIntoPhase = Math.max(0, elapsedDays - phaseStartDays);
      
      // Days remaining in phase
      const phaseEndDays = phaseEndMonth * 30.44;
      const daysRemainingInPhase = Math.max(0, phaseEndDays - elapsedDays);
      
      // Phase progress percentage
      const phaseDuration = phaseMonths * 30.44;
      const phaseProgress = Math.min(100, Math.max(0, (daysIntoPhase / phaseDuration) * 100));

      // Determine next phase
      const currentPhaseIndex = PHASE_ORDER.indexOf(currentPhase);
      const nextPhase = currentPhaseIndex < PHASE_ORDER.length - 1 
        ? PHASE_ORDER[currentPhaseIndex + 1] 
        : null;

      // Check if transitioning (within last week of phase)
      const isTransitioning = daysRemainingInPhase <= 7 && nextPhase !== null;

      const newResult: PerformancePhaseResult = {
        currentPhase,
        monthsElapsed: calculatedMonthsElapsed,
        totalMonths: calculatedTotalMonths,
        daysIntoPhase: Math.round(daysIntoPhase),
        daysRemainingInPhase: Math.round(daysRemainingInPhase),
        phaseProgress: Math.round(phaseProgress),
        nextPhase,
        isTransitioning
      };

      setResult(newResult);
      
      logger.info('Performance phase calculated', {
        currentPhase,
        monthsElapsed: calculatedMonthsElapsed,
        phaseProgress: Math.round(phaseProgress),
        isTransitioning
      });
    } catch (error) {
      logger.error('Failed to calculate performance phase', error);
    }
  }, [focus]);

  return result;
};

// Utility function to get phase details
export const getPhaseDetails = (phase: PerformancePhase) => {
  const phaseMap = {
    planning: {
      label: 'Planning',
      icon: '📋',
      description: 'Set goals and define metrics',
      keyActivities: [
        'Define annual goals',
        'Set baseline metrics',
        'Align with manager'
      ],
      tips: [
        'Be specific and measurable with goals',
        'Get early feedback from your manager',
        'Consider both technical and soft skills'
      ]
    },
    foundation: {
      label: 'Foundation',
      icon: '🏗️',
      description: 'Build momentum and establish routines',
      keyActivities: [
        'Establish workflows',
        'Quick wins',
        'Build relationships'
      ],
      tips: [
        'Focus on early wins to build confidence',
        'Set up regular check-ins',
        'Document your process improvements'
      ]
    },
    execution: {
      label: 'Execution',
      icon: '⚡',
      description: 'Drive results and track progress',
      keyActivities: [
        'Major deliverables',
        'Track metrics',
        'Mid-year check-in'
      ],
      tips: [
        'Keep momentum with consistent progress',
        'Update metrics monthly',
        'Seek feedback proactively'
      ]
    },
    impact: {
      label: 'Impact',
      icon: '🎯',
      description: 'Demonstrate achievements',
      keyActivities: [
        'Showcase results',
        'Gather feedback',
        'Document wins'
      ],
      tips: [
        'Quantify your impact where possible',
        'Collect testimonials from stakeholders',
        'Prepare case studies of key projects'
      ]
    },
    review_prep: {
      label: 'Review Prep',
      icon: '📝',
      description: 'Prepare for review meeting',
      keyActivities: [
        'Complete self-review',
        'Gather peer feedback',
        'Prepare talking points'
      ],
      tips: [
        'Start self-review early',
        'Practice your key messages',
        'Prepare questions for your manager'
      ]
    }
  };

  return phaseMap[phase];
};